import "./PollyAuthoringPage.scss";

import {
  Button,
  ChevronStartIcon,
  ExclamationTriangleIcon,
  Flex,
  Text,
} from "@fluentui/react-northstar";
import { dialog, DialogDimension } from "@microsoft/teams-js";
import { PollyAuthoringOrigin, TeamsSurveyTypes } from "@teams/types";
import React, { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";

import { useUserInfo } from "../../../../_shared/actions/useUserInfo";
import { AgileCheckout } from "../../../../_shared/components/Checkout/AgileCheckout";
import { RequestLicenseButton } from "../../../../_shared/components/Checkout/RequestLicenseButton";
import { ResponseUsageMeter } from "../../../../_shared/components/MeterBars/ResponseUsageMeter";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";
import { isMobileClient } from "../../../../_shared/utils/clientHelper";
import { clientEventTracker } from "../../../../_shared/utils/clientTelemetry";
import { getCheckOutTaskInfo } from "../../../../_shared/utils/startTaskWrapper";
import { getPageContext } from "../../../../_shared/utils/teamsHelpers";
import { useInterval } from "../../../../_shared/utils/useInterval";
import { isTrialAvailable, userIsOverUsageLimit } from "../../../../_shared/utils/userHelpers";
import { PollyAuthoring } from "../PollyAuthoring";
import { PollySurveyAuthoringProps } from "../PollySurveyAuthoringForm";
import useWindowDimensions from "../PollySurveyAuthoringForm/useWindowDimensions";

export const PollyAuthoringPage: React.FC = () => {
  const { type } = useParams<{ type: TeamsSurveyTypes }>();
  const history = useHistory();
  const { search } = useLocation();
  const { teamsContext } = useTeamsContext();
  const { user } = useUserInfo();
  const context = getPageContext();

  const urlSearchParams = useMemo(() => {
    const params = new URLSearchParams(search);

    const error = params.get("error");
    const draft = params.get("draftId");
    const tid = params.get("templateId");
    const settings = params.get("settings");
    const mid = params.get("messageid");
    const trackingSource = params.get("trackingSource");
    const authoringOrigin = params.get("authoringOrigin") as PollyAuthoringOrigin | null;
    const surveyId = params.get("surveyId");
    const reOpen = params.get("reOpen");
    const duplicate = Boolean(params.get("duplicate"));
    const skipInstrumentation = Boolean(params.get("skipInstrumentation"));
    const isPersonal = Boolean(params.get("isPersonal"));
    const customRecipient = Boolean(params.get("customRecipient"));

    return {
      draftId: draft ?? undefined,
      templateId: tid ?? undefined,
      externalErrors: error ?? undefined,
      messageId: mid ?? undefined,
      trackingSource: trackingSource ?? undefined,
      authoringOrigin: authoringOrigin ?? undefined,
      skipInstrumentation,
      isPersonal,
      surveyId: surveyId ?? undefined,
      reOpen: reOpen ? reOpen === "true" : undefined,
      ...(settings && {
        authoringSettings: JSON.parse(settings) as Partial<PollySurveyAuthoringProps>,
      }),
      isEdit: Boolean(surveyId) && !duplicate,
      duplicate: duplicate ?? undefined,
      customRecipient,
    };
  }, [search]);

  const {
    draftId,
    templateId,
    externalErrors,
    authoringSettings,
    messageId,
    trackingSource,
    authoringOrigin,
    skipInstrumentation,
    isPersonal,
    surveyId,
    reOpen,
    isEdit,
    duplicate,
    customRecipient,
  } = urlSearchParams;

  const cancelAction = useCallback(() => {
    if (history.action !== "POP") {
      history.goBack();
    } else {
      history.replace("/msteams/content/start");
    }
  }, [history]);

  const isAgile = context === "/agile";
  const hasAgileSubscription = user.subscriptions.some(
    sub => sub.plan === "agile-standup" && sub.state === "active"
  );

  if (isAgile && !hasAgileSubscription) {
    return (
      <div className="polly-authoring-page">
        <AgileUpgradePage />
      </div>
    );
  }

  const isCore = context === "/msteams";
  const hasCoreSubscription = !(
    userIsOverUsageLimit(user) &&
    !user.subscriptions.some(sub => sub.state === "active" && sub.type === "full")
  );

  if (isCore && !hasCoreSubscription) {
    return (
      <div className="polly-authoring-page">
        <CoreUpgradePage onCancel={cancelAction} />
      </div>
    );
  }

  return (
    <Flex
      column
      className={reOpen ? "polly-authoring-page__reopen-page" : "polly-authoring-page"}
      gap="gap.smaller"
    >
      <PollyAuthoring
        type={type}
        draftId={draftId}
        templateId={templateId}
        externalError={externalErrors}
        formSettings={authoringSettings}
        messageId={messageId}
        trackingSource={trackingSource ?? "unknown"}
        authoringOrigin={authoringOrigin ?? "tab"}
        isPersonal={isPersonal}
        skipInstrumentation={skipInstrumentation}
        surveyId={surveyId}
        reOpen={reOpen}
        isEdit={isEdit}
        duplicate={duplicate}
        showHeader
        customRecipient={customRecipient}
        onCreated={(usedTaskSubmit, pollyId, wasUpdate, isRecurring) => {
          if (!usedTaskSubmit && teamsContext.page.frameContext === "task") {
            if (getPageContext() === "/agile") {
              dialog.url.submit();
            } else {
              let url = `/msteams/content/polly${
                pollyId ? "/" + encodeURIComponent(pollyId) : ""
              }/success`;

              const params = new URLSearchParams();

              if (wasUpdate) {
                params.set("update", "true");
              }

              if (isRecurring) {
                params.set("recurring", "true");
              }

              if (wasUpdate || isRecurring) {
                url = `${url}?${params.toString()}`;
              }
              history.replace(url);
            }
          }
        }}
      />
    </Flex>
  );
};

const CoreUpgradePage: React.FC<{ onCancel: () => void }> = ({ onCancel }) => {
  const { user, loadUser } = useUserInfo();
  const history = useHistory();
  const { teamsContext } = useTeamsContext();
  const [upgradeSelected, setUpgradeSelected] = useState<boolean>();
  const { width } = useWindowDimensions();

  useInterval(() => {
    if (!upgradeSelected) {
      return;
    }

    loadUser(true);
  }, 5000);

  const trialAvailable = isTrialAvailable(user, "msteams-standard");
  const licenseRequest =
    trialAvailable && user.featureFlags.enterpriseTenant && getPageContext() === "/msteams";
  const isMobile = isMobileClient(teamsContext);

  return (
    <Flex column hAlign="center" gap="gap.medium">
      <Flex.Item align="stretch">
        <div>
          <Button
            text
            content={
              <Flex vAlign="center">
                <ChevronStartIcon xSpacing="after" />
                Back
              </Flex>
            }
            onClick={onCancel}
          />
        </div>
      </Flex.Item>

      <Flex>
        <ExclamationTriangleIcon size="large" xSpacing="after" />
        <Text size="large" weight="bold" content="Monthly response limit reached!" />
      </Flex>

      <ResponseUsageMeter />

      <Text
        size="large"
        content={
          licenseRequest
            ? "Request a license to continue using Polly"
            : "Please upgrade to continue using Polly"
        }
      />

      {!isMobile && (
        <>
          {licenseRequest && (
            <RequestLicenseButton plan="msteams-standard" onDone={() => loadUser()} />
          )}
          {!licenseRequest && (
            <Button
              className="UsageMeterContainer__cta-button"
              content={
                <Text
                  content={
                    isTrialAvailable(user, "msteams-standard")
                      ? `Start ${user.featureFlags.trialDays}-day trial`
                      : "Upgrade plan"
                  }
                  weight="bold"
                />
              }
              primary
              onClick={() => {
                setUpgradeSelected(true);
                clientEventTracker.track("UpgradeButton clicked", user, {
                  trackingSource: "authoring page",
                });

                const checkOutTaskInfo = getCheckOutTaskInfo(
                  isTrialAvailable(user, "msteams-standard"),
                  "authoring page",
                  width
                );
                dialog.update.resize({ height: checkOutTaskInfo.height || DialogDimension.Medium , width: checkOutTaskInfo.width || DialogDimension.Medium});
                history.push(checkOutTaskInfo.url!);
              }}
            />
          )}
        </>
      )}
      {isMobile && <Text>To continue using Polly please open Teams on your desktop</Text>}
    </Flex>
  );
};

const AgileUpgradePage: React.FC = () => {
  const { loadUser } = useUserInfo();

  return (
    <AgileCheckout
      trackingSource="agile-authoring"
      onCompleted={() => {
        loadUser();
      }}
    />
  );
};
